import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from './Footer';
import API_BASE_URL from '../utils/config';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Blog content styling with enhanced colors and visuals
  const blogStyles = `
    .blog-content {
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
      font-family: 'Inter', sans-serif;
      line-height: 1.8;
      color: #374151;
      background-color: #fff;
      border-radius: 1rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .blog-content h1 {
      font-size: 2.5rem;
      font-weight: 800;
      margin: 2rem 0;
      color: #0f172a;
      border-bottom: 3px solid #10b981;
      padding-bottom: 0.5rem;
      display: inline-block;
    }

    .blog-content h2 {
      font-size: 2rem;
      font-weight: 700;
      margin: 2.25rem 0 1.5rem;
      color: #1e293b;
      padding-left: 1rem;
      border-left: 4px solid #3b82f6;
    }

    .blog-content h3 {
      font-size: 1.5rem;
      font-weight: 600;
      margin: 2rem 0 1.25rem;
      color: #334155;
      position: relative;
    }
    
    .blog-content h3::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 3px;
      background-color: #6366f1;
    }

    .blog-content p {
      margin: 1.5rem 0;
      line-height: 1.9;
    }

    .blog-content ul, .blog-content ol {
      margin: 1.75rem 0;
      padding-left: 1.25rem;
    }

    .blog-content ul {
      list-style: none;
    }

    .blog-content ul li {
      position: relative;
      padding-left: 2.5rem;
      margin: 1rem 0;
      color: #374151;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }

    .blog-content ul li::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.5rem;
      width: 1.5rem;
      height: 1.5rem;
      background-color: #10b981;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .blog-content ul li::after {
      content: '✔';
      position: absolute;
      left: 0.4rem;
      top: 0.5rem;
      color: white;
      font-size: 0.9rem;
      font-weight: bold;
    }

    .blog-content ul li:hover {
      transform: translateX(5px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .blog-content ol {
      list-style: none;
      counter-reset: item;
    }

    .blog-content ol li {
      counter-increment: item;
      position: relative;
      padding-left: 2.5rem;
      margin: 1rem 0;
      color: #374151;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }

    .blog-content ol li::before {
      content: counter(item);
      position: absolute;
      left: 0;
      top: 0.25rem;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      background: linear-gradient(to right, #3b82f6, #10b981);
      color: white;
      font-size: 0.875rem;
      font-weight: 600;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .blog-content ol li:hover {
      transform: translateX(5px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .blog-content table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      margin: 2.5rem 0;
      background: linear-gradient(to bottom, #ffffff, #f8fafc);
      border-radius: 0.75rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      overflow: hidden;
    }

    .blog-content table th {
      background: linear-gradient(to right, #1e3a8a, #3b82f6);
      font-weight: 700;
      text-align: left;
      padding: 1.5rem;
      color: #ffffff;
      border: none;
      font-size: 1.1rem;
      text-transform: uppercase;
      letter-spacing: 0.05rem;
      position: relative;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .blog-content table th::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: #10b981;
    }

    .blog-content table th:first-child {
      border-top-left-radius: 0.75rem;
    }

    .blog-content table th:last-child {
      border-top-right-radius: 0.75rem;
    }

    .blog-content table td {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid #e5e7eb;
      font-size: 0.95rem;
      color: #374151;
      position: relative;
    }

    .blog-content table tr:last-child td {
      border-bottom: none;
    }

    .blog-content table tr:last-child td:first-child {
      border-bottom-left-radius: 0.75rem;
    }

    .blog-content table tr:last-child td:last-child {
      border-bottom-right-radius: 0.75rem;
    }

    .blog-content table tr:nth-child(even) {
      background-color: #f1f5f9;
    }

    .blog-content table tr:nth-child(odd) {
      background-color: #ffffff;
    }

    .blog-content table tr:hover td {
      background-color: #e0f2fe;
      transition: background-color 0.3s ease;
    }

    .blog-content table td::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 50%;
      background-color: #10b981;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .blog-content table tr:hover td::before {
      opacity: 1;
    }

    .blog-content img {
      max-width: 100%;
      height: auto;
      margin: 2.5rem auto;
      display: block;
      border-radius: 0.75rem;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
      transition: transform 0.3s ease;
    }

    .blog-content img:hover {
      transform: scale(1.02);
    }

    .blog-content blockquote {
      border-left: 6px solid #10b981;
      padding: 1.5rem;
      margin: 2rem 0;
      background-color: #f0fdf4;
      border-radius: 0 0.5rem 0.5rem 0;
      font-style: italic;
      position: relative;
    }

    .blog-content blockquote::before {
      content: '"';
      font-size: 3rem;
      color: #10b981;
      opacity: 0.3;
      position: absolute;
      top: -10px;
      left: 10px;
      font-family: Georgia, serif;
    }

    .blog-content code {
      background-color: #f1f5f9;
      padding: 0.2rem 0.4rem;
      border-radius: 0.25rem;
      font-family: 'Fira Code', monospace;
      font-size: 0.875rem;
      color: #6366f1;
      border: 1px solid #e2e8f0;
    }

    .blog-content pre {
      background-color: #1e293b;
      color: #f8fafc;
      padding: 1.5rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      margin: 1.75rem 0;
      border-left: 4px solid #6366f1;
    }

    .blog-content pre code {
      background-color: transparent;
      color: inherit;
      padding: 0;
      border: none;
    }

    .blog-content a {
      color: #0ea5e9;
      text-decoration: none;
      border-bottom: 1px dashed #0ea5e9;
      transition: all 0.2s ease;
    }

    .blog-content a:hover {
      color: #0284c7;
      border-bottom: 1px solid #0284c7;
    }

    @media (max-width: 768px) {
      .blog-content {
        padding: 1.25rem;
        border-radius: 0.75rem;
      }

      .blog-content h1 {
        font-size: 2rem;
      }

      .blog-content h2 {
        font-size: 1.75rem;
      }

      .blog-content h3 {
        font-size: 1.25rem;
      }

      .blog-content table {
        font-size: 0.875rem;
      }

      .blog-content table th, .blog-content table td {
        padding: 1rem;
      }

      .blog-content table th {
        font-size: 0.95rem;
      }

      .blog-content ul li, .blog-content ol li {
        padding-left: 2rem;
      }

      .blog-content ul li::before, .blog-content ol li::before {
        width: 1.25rem;
        height: 1.25rem;
        font-size: 0.75rem;
      }

      .blog-content ul li::after {
        left: 0.3rem;
        top: 0.4rem;
        font-size: 0.8rem;
      }
    }
  `;

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/blogs/${id}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Blog not found');
          }
          throw new Error('Failed to fetch blog');
        }
        const data = await response.json();
        setBlog(data);
        
        // Update URL with blog title only (removed ID)
        const titleSlug = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const newUrl = `/blog/${titleSlug}`; // Updated to remove ID
        if (window.location.pathname !== newUrl) {
          window.history.replaceState({}, '', newUrl);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <div className="text-red-600 text-xl mb-4">Error: {error}</div>
        <button
          onClick={() => navigate('/blog')}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors duration-300"
        >
          Back to Blogs
        </button>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <div className="text-gray-600 text-xl mb-4">Blog not found</div>
        <button
          onClick={() => navigate('/blog')}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors duration-300"
        >
          Back to Blogs
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 mt-20">
      <style>{blogStyles}</style>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Blog Header */}
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 relative">
            {blog.title}
            <span className="block h-1 w-32 bg-green-500 mx-auto mt-4"></span>
          </h1>
          <div className="flex items-center justify-center text-gray-600 space-x-4">
            <span>
              Published on {new Date(blog.created_at).toLocaleDateString()}
            </span>
            <span>•</span>
            <span>{blog.views || 0} views</span>
          </div>
        </header>

        {/* Blog Content */}
        <div className="blog-content">
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">{blog.description}</p>
          <div dangerouslySetInnerHTML={{ __html: blog.content }} />
        </div>
        
        {/* Back to blogs button */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/blogs')}
            className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors duration-300 font-medium"
          >
            Back to All Blogs
          </button>
        </div>
      </article>
      <Footer />
    </div>
  );
};

export default BlogPost;